import {
    init,
    setUser,
    setTags,
    EventHint,
    browserTracingIntegration,
    globalHandlersIntegration,
    captureConsoleIntegration,
    captureMessage,
    captureException,
    ErrorBoundary as SentryErrorBoundary,
} from '@sentry/react';
import {
    isSafari,
    isFeiShuMobile,
    isHeyTapBrowser,
    isMobileLenovoBrowser,
    isPinganZhiniaoWebview,
    isQQBrowser,
    isEdge,
    isUMUWebview,
    isWxChat,
    isDesktop,
    isDingding,
    isDingdingDesktop,
} from '@umu/umu-ua';
import { getData } from '@umu/umu-util';
import { getCurrentSystem, isProd } from '@umu/ukit-lang';
import { getModuleData } from 'common/src/services';
import { getConfig } from '@umu/umu-api';
import { wrapErrorByMessage } from 'businessCommon/src/services/error';
import { createElement } from 'react';
import { safeJSONParse } from '../@utils/jsonParse';

const CurrentSystem = getCurrentSystem();
const SYSTEM_DNS_URL_MAP = {
    CN: 'https://de5cbf34dddb43e7a09553fb808cfd57@sentry.umu.cn/11',
    TW: 'https://3c30774713ac402fbe62cf5d4ff30886@sentry.umu.tw/4',
    JP: 'https://a124083f17e4411ab12341704975b8b7@sentry.umu.co/13',
    EN: 'https://3ee9857afe7345c8b5d6c484667a1a82@sentry.umu.com/4',
    EM: 'https://81c588d062bf4651b745442385f039ee@sentry.umu.com/14',
    ATES: '',
};

// 从apollo 获取 traces_sample_rate 的类
class TracesSampleRate {
    private umuId: string;
    constructor(umuId: string) {
        this.umuId = umuId;
    }
    // get default traces sample rate
    private getDefaultTracesSampleRate = () => {
        let tracesSampleRate = 0.5;
        if (CurrentSystem === 'JP') {
            tracesSampleRate = 0.05;
        }
        return tracesSampleRate;
    };

    private async getTracesSampleRateConfig() {
        // 判断 userId 是否为空
        if (!this.umuId) {
            return null;
        }
        try {
            const res = await getConfig({ key: `sentry_all_log_open` });
            if (res?.data) {
                // 解析res.data， data 是 userid: traces_sample_rate 的格式
                const data = safeJSONParse(res.data, {} as Record<string, number>);
                return Number(data?.[this.umuId]);
            }
            return null;
        } catch (error) {
            console.error('[Sentry] getTracesSampleRateConfig error', error);
            return null;
        }
    }
    public getTracesSampleRate = async () => {
        const rate = await this.getTracesSampleRateConfig();
        if (!rate || isNaN(rate) || rate < 0 || rate > 1) {
            return this.getDefaultTracesSampleRate();
        }
        return rate;
    };
}

// get sentry release
const getSentryRelease = () => {
    const version = (window as any).version;
    const sentryRelease = `mono_${version}`;
    return sentryRelease;
};

// get deny urls
const getDenyUrls = () => {
    return [/^chrome:/i, /chrome-extension:/i, /webkit-masked-url:\/\/hidden/i];
};

// 临时忽略错误 - 音视频相关的报错
const IgnoreVideoErrorsList = [
    'NotSupportedError: The element has no supported sources.',
    'The operation is not supported.',
    'NotAllowedError: Permission dismissed',
    'NotAllowedError: Permission denied',
    'NotReadableError: Could not start audio source',
    'NotSupportedError: Failed to load because no supported source was found.',
    "NotAllowedError: play() failed because the user didn't interact with the document first. https://goo.gl/xX8pDD",
    'NotReadableError: Could not start video source',
    'NotFoundError: Requested device not found',
    'NotSupportedError: Not supported',
];

// 临时忽略基础错误
const IgnoreBaseErrorsList = ['请求超时。'];

// get ignore errors list
const getIgnoreErrorsList = () => {
    return [
        'window.ttJSBridge is undefined', // 飞书
        'Java object is gone',
        "Can't find variable: WeakRef", // 微信小程序内报错，先忽略
        "Can't find variable: lk_isInPIP",
        "Can't find variable: zaloJSV2",
        "Can't find variable: lk_pauseAllMedia",
        'zaloJSV2 is not defined',
        "Can't find variable: lk_pauseAllMedia",
        'handler is not a function',
        'WebKit 遇到内部错误',
        'The video element is not ready to enter the Picture-in-Picture mode.',
        'InvalidStateError: The object is in an invalid state.',
        'LIDNotify is not defined',
        "Can't find variable: UCReadabilityContext",
        'UCShellJava.sdkEventFire is not a function', // uc 浏览器错误
        'https://statics-cdn-cn.umucdn.cn/statics/js/pdf/pdf.min.2.12.js', // pdf 加载失败，一般是切换页面导致的
        'InvalidStateError: The object is in an invalid state.', // video内部播放错误
        'NotAllowedError: play() can only be initiated by a user gesture.', // video 播放错误
        'err null',
        'domtoimage: Error while reading CSS rules from',
        'Member PerformanceObserverInit.entryTypes is required and must be an instance of sequence',
        'SMGNativeJS is not defined',
        'onConfigurationInit is not defined',
        'The video element is processing a Picture-in-Picture request',
        'cdn.huiju.cool',
        '(error: https://cdn.huiju.cool/js/20240415/clab_chunk.js)',
        '[KEKEAD str]', //https://app.asana.com/0/51340828583966/1209726452453875/f
        '__WeixinJSBridgeNativeProxy__ not found',
        'do not belong to channel host',
        'makeWeikeStatusErrorLog', // https://app.asana.com/0/0/1209725751413595/f
        'LIDNotifyId is not defined',
        'Setting up fake worker failed', // https://app.asana.com/0/0/1209711855434494/f
        'WeixinJSBridge',
        '[video-takeover-extract]',
        'empty host rule',
        "Can't find variable: WebViewJavascriptBridge",
        'WebViewJavascriptBridge is not defined',
        "Can't find variable: WeixinJSBridge",
        "Can't find variable: LarkWebViewJavaScriptBridge",
        'AbortError: The operation was aborted.',
        'Load failed',
        'TypeError: Load failed',
        'Failed to fetch',
        'TypeError: Failed to fetch',
        'No error message',
        'The network connection was lost.',
        "Can't find variable: cordova",
        'appcb is not defined',
        '網路連線中斷。',
        '网络连接已中断',
        '已取消',
        'Internet連線已斷開。',
        /^AbortError: The play/i,
        /^NotAllowedError: The request is not/i,
        /^The fetching process for the media resource was aborted/i,
        'ChunkLoadError',
        'The above error occurred in task',
        'Please use the NodeViewWrapper component for your node view',
        'Java bridge method invocation error',
        'LanxinJsBridge is not defined',
        "Can't find variable: LanxinJsBridge",
    ]
        .concat(IgnoreVideoErrorsList)
        .concat(IgnoreBaseErrorsList);
};

let dynamicSentryIgnoreErrors: string[] = [];

const initDynamicSentryIgnoreErrors = async () => {
    try {
        const res = await getConfig({ key: `sentry_ignore_list_url` });
        const url = res?.data || '';
        if (url) {
            const res = await fetch(url);
            const data = await res.json();
            if (data && Array.isArray(data)) {
                dynamicSentryIgnoreErrors = data;
            }
        }
    } catch (error) {
        console.error('initDynamicSentryIgnoreErrors error', error);
    }
};

const beforeSend = (event: any, _hint: EventHint) => {
    // filter out UnhandledRejection errors that have no information https://github.com/getsentry/sentry-javascript/issues/3440
    const ignoreErrorList = [
        'Please use the NodeViewWrapper component for your node view.',
        "'get' on proxy: property 'javaEnabled' is a read-only",
        'Loading chunk 1 failed.',
        "Can't find variable: Aplus4Native",
        'The above error occurred in task', // saga报错，和 Load failed 是级联问题
        // 'hmErrorLog2', // 日志机制，需要从日志处去掉
        'Java bridge method invocation error',
        "undefined is not an object (evaluating 'window.webkit.messageHandlers')",
        'window.weixinPostMessageHandlers.weixinDispatchMessage.postMessage',
        "Failed to execute 'sendBeacon' on 'Navigator'",
        "Failed to execute 'replaceState' on 'History'",
        'Invalid PDF structure.',
        "Can't find variable: QK_middlewareReadModePageDetect",
        'nativeInitedNotify is not defined',
        '[video-takeover] window.BrowserStat.generalStat',
        // https://sentry.umu.cn/organizations/sentry/issues/64577453/?project=11&query=is%3Aunresolved&referrer=issue-stream
        // 原因：课程获取学习证书时会有一个撒花特效，是这个 react-canvas-confetti 库提供的，它里面会通过 webWorker 来绘制，应该是它内部逻辑的问题，先忽略
        'e.getBoundingClientRect is not a function',
        // https://sentry.umu.cn/organizations/sentry/issues/213048714/?project=8&project=11&project=20&query=is%3Aunresolved&referrer=issue-stream&sort=user&statsPeriod=14d
        // 过程点评获取麦克风权限报错，这个需要用户主动授权，页面已经有比较好的提示了，先忽略
        '[timeline] [useRecorder] getUserMedia error NotAllowedError: Permission dismissed',
        // https://app.asana.com/1/45778247595893/project/1200987305187523/task/1210094969855292?focus=true
        // 来自于腾讯云文档：由于视频文件损坏或是该视频使用了你的浏览器不支持的功能，播放终止。
        // 我们没啥好解决的，直接在代码 sentry 忽略吧
        'he media playback was aborted due to a corruption problem or because the media used features your browser did not support',
        // TCPlayer 的报错先忽略吧，难复现&难排查
        // https://app.asana.com/1/45778247595893/project/1200987305187523/task/1210094969855291?focus=true
        'TCPlayer: ERROR: (CODE:',
        // 怀疑是某个用户的 qq 浏览器的插件问题，先忽略它
        'Error during distillation: TypeError: Illegal invocation e.stack:TypeError: Illegal invocation',
        // 可能是用户 浏览器插件 注入了 Veeva CLM 相关代码，先忽略
        "undefined is not an object (evaluating 'com.veeva.clm.updateCurrentRecordsOnExit')",
        // WeakRef 在某些环境下不支持，且无法完美 polyfill, 原因：弱引用特性依赖 GC，JS 层面无法模拟. 先忽略
        'WeakRef is not defined',
    ].concat(dynamicSentryIgnoreErrors);

    if (window.location.protocol === 'file:') {
        console.warn('[Sentry] 忽略 file:// 环境下的错误');
        return null; // 返回 null 会阻止上报
    }

    if (
        event !== undefined &&
        event.exception !== undefined &&
        event.exception.values !== undefined &&
        event.exception.values.length == 1
    ) {
        const e = event.exception.values[0] || {};
        const exceptionMessage = e.value || '';

        if (
            e.type === 'UnhandledRejection' &&
            exceptionMessage.indexOf('Non-Error promise rejection captured with value:') > -1
        ) {
            return null;
        }
        // 忽略 [object Event] 错误
        if (e.type === 'Event') {
            return null;
        }
        // 忽略一些错误
        if (ignoreErrorList.some((error) => exceptionMessage.indexOf(error) > -1)) {
            return null;
        }
        // 忽略 HeyTap 浏览器的 err null 错误
        if (isHeyTapBrowser && exceptionMessage.indexOf('err null') > -1) {
            return null;
        }
        // 忽略 MobileLenovoBrowser 浏览器的 nativeInitedNotify is not defined 错误
        if (isMobileLenovoBrowser && exceptionMessage.indexOf('nativeInitedNotify is not defined') > -1) {
            return null;
        }
        // 忽略 PinganZhiniao webview 的 lifecycle is not defined错误
        if (isPinganZhiniaoWebview && exceptionMessage.indexOf('lifecycle is not defined') > -1) {
            return null;
        }
        if (
            exceptionMessage.indexOf('[android]:WebViewJavascriptBridge: WARNING: javascript handler threw.') > -1 ||
            exceptionMessage.indexOf('WebViewJavascriptBridge: WARNING: no handler for message from ObjC') > -1
        ) {
            return null;
        }
        if (isQQBrowser && exceptionMessage.indexOf('news_page') > -1) {
            return null;
        }
        // Can't find variable: func_1744872163092_4465
        if (isFeiShuMobile && exceptionMessage.indexOf("Can't find variable: func_") > -1) {
            return null;
        }
        if (exceptionMessage.indexOf('domtoimage: Error while reading CSS rules from') > -1) {
            return null;
        }
        if (isEdge && exceptionMessage.indexOf("Cannot read properties of null (reading 'rule')") > -1) {
            return null;
        }
        // https://app.asana.com/1/45778247595893/project/1203622320206294/task/1210094967797989?focus=true
        if (
            isSafari &&
            exceptionMessage.indexOf('Connection to Indexed Database server lost. Refresh the page to try again') > -1
        ) {
            return null;
        }
        if (exceptionMessage.indexOf('/js-sdk/collect-0.0.5.min.js') > -1) {
            return null;
        }
    }
    return event;
};

const getEnvironment = (isProd: boolean, tracesSampleRate: number) => {
    if (isProd && tracesSampleRate === 1) {
        return 'canary-full-trace';
    }
    return isProd ? 'pro' : 'dev';
};

const getJsContext = () => {
    if (isUMUWebview) {
        return 'umuapp';
    }
    if (isDesktop) {
        return 'desktop';
    }
    if (isDingdingDesktop) {
        return 'dingdingdesktop';
    }
    if (isWxChat) {
        return 'wxchat';
    }
    if (isDingding) {
        return 'dingding';
    }
    if (isFeiShuMobile) {
        return 'feishu-mobile';
    }
    if (isQQBrowser) {
        return 'qqbrowser';
    }
    if (isEdge) {
        return 'edge';
    }
    return 'browser';
};

function moduleEntrySort(moduleA: string[], moduleB: string[]) {
    const MODULE_PRIORITY_ORDER = ['twww', 'platform'];
    const indexA = MODULE_PRIORITY_ORDER.indexOf(moduleA[0]);
    const indexB = MODULE_PRIORITY_ORDER.indexOf(moduleB[0]);

    // 将 -1 (不在列表中) 替换为列表长度，确保排到最后
    const priorityA = indexA === -1 ? MODULE_PRIORITY_ORDER.length : indexA;
    const priorityB = indexB === -1 ? MODULE_PRIORITY_ORDER.length : indexB;

    // 否则按优先级数字排序（数字越小优先级越高）
    return priorityA - priorityB;
}
/**
 * 获取当前页面的作用域信息
 */
const getPageScope = () => {
    try {
        // 例如：{ "learning_task/my-task": {}, "twww/courseManage": {}, "platform/platform": {} }
        const langObj = (window as any)?.__LANG__ || {};
        if (!langObj || typeof langObj !== 'object' || Object.keys(langObj).length === 0) {
            return { repo: '', pageScope: '' };
        }
        const moduleEntries = Object.keys(langObj)
            .map((key) => {
                return key.split('/');
            })
            .filter((keys) => keys.length > 1)
            .sort(moduleEntrySort);
        if (moduleEntries.length === 0) {
            return { repo: '', pageScope: '' };
        }
        const [repo, pageScope] = moduleEntries[0];
        return {
            repo: repo || '',
            pageScope: pageScope || '',
        };
    } catch (error) {
        // 异常处理：避免页面作用域获取失败影响整个 Sentry 初始化流程
        console.error('[Sentry] getPageScope error:', error);
        return { repo: '', pageScope: '' };
    }
};

const getSentryParams = () => {
    const userInfo = getModuleData('userInfo');
    const eId = getData(userInfo, 'enterprise_info', 'enterprise_id') || '0';
    const eName = getData(userInfo, 'enterprise_info', 'show_name') || '';
    const userName = userInfo?.user_name || '';
    const email = userInfo?.email || '';
    const umuId = userInfo?.umu_id || '';
    const jsContext = getJsContext();
    const params: Record<string, string> = {
        eId,
        eName,
        umuId,
        userName,
        email,
        jsContext,
        pageScope: getPageScope().pageScope,
        repo: getPageScope().repo,
    };
    return params;
};

// 初始化Sentry配置
const initSentryConfig = (
    tracesSampleRate: number,
    replaysSessionSampleRate: number,
    replaysOnErrorSampleRate: number,
) => {
    const sentryDSN = SYSTEM_DNS_URL_MAP[CurrentSystem as keyof typeof SYSTEM_DNS_URL_MAP] || '';
    if (!sentryDSN) {
        return;
    }
    initDynamicSentryIgnoreErrors();
    init({
        environment: getEnvironment(isProd, tracesSampleRate),
        dsn: sentryDSN,
        integrations: [
            browserTracingIntegration(),
            globalHandlersIntegration({
                onunhandledrejection: true,
                onerror: true,
            }),
            captureConsoleIntegration({ levels: ['error'] }),
        ],
        ignoreErrors: tracesSampleRate === 1 ? [] : getIgnoreErrorsList(),
        denyUrls: getDenyUrls(),
        // Set tracesSampleRate to 1.0 to capture 100% of transactions for performance monitoring.
        // We recommend adjusting this value in production
        release: getSentryRelease(),
        maxBreadcrumbs: 50,
        tracesSampleRate: tracesSampleRate,
        replaysSessionSampleRate: replaysSessionSampleRate,
        replaysOnErrorSampleRate: replaysOnErrorSampleRate,
        // https://docs.sentry.io/platforms/javascript/configuration/filtering/
        beforeSend: tracesSampleRate === 1 ? (event: any, _hint: EventHint) => event : beforeSend,
    });
    // window.Sentry = Sentry;
    // 目前全局只有两个方法上报错误，有需要再在window中注入
    (window as any).Sentry = {
        captureException: captureException,
        captureMessage: captureMessage,
    };

    const params = getSentryParams();
    setUser(params);
    setTags(params);
};

export const initSentry = (umuId: string) => {
    let replaysSessionSampleRate = 0;
    let replaysOnErrorSampleRate = 0;
    const tracesSampleRate = new TracesSampleRate(umuId);
    tracesSampleRate
        .getTracesSampleRate()
        .then((tracesSampleRate) => {
            // 同时设置回放设置
            if (tracesSampleRate == 1) {
                replaysSessionSampleRate = 1;
                replaysOnErrorSampleRate = 1;
            }
            initSentryConfig(tracesSampleRate, replaysSessionSampleRate, replaysOnErrorSampleRate);
        })
        .catch((error) => {
            console.error('[Sentry] initSentry error', error);
        });
};

export function captureSentryException(error: Error, message: string = '', extra: Record<string, any> = {}): void {
    if (!error) {
        return;
    }
    const wrappedError = wrapErrorByMessage(error, message);
    if (window.Sentry) {
        window.Sentry.captureException(wrappedError, {
            extra: {
                errorResource: error,
                ...extra,
            },
        });
    }
}

export const wrapWithErrorBoundary = (children: React.ReactNode) => {
    // 收集错误信息并发送到Sentry
    return createElement(
        SentryErrorBoundary,
        {
            beforeCapture: (scope) => {
                // 设置统一的标签
                scope.setTag('errorSource', 'ReactErrorBoundary');
            },
        },
        children,
    );
};

export const ErrorBoundary = ({ children }: { children: React.ReactNode }) => {
    return wrapWithErrorBoundary(children);
};
